import { ReactElement, useEffect, useRef, useState } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import { Drawer } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';

import { ApiRoute, Route } from 'config';
import { patch } from 'utils/network';
import { postMessage, WebViewMessage } from 'utils/webview';
import useCurrentUser from 'hooks/useCurrentUser';
import RadioButton from 'components/shared/RadioButton';
import { useNavigate } from 'react-router-dom';
import { cancelOptions, getOptionsForCancel } from 'platform/modals/subscription.types';
import CancelRebuttalModal from './CancelRebuttalModal';
import styles from './CancelModalContainer.module.scss';

type CancelStep = 'REASON_CAPTURE' | 'REBUTTAL' | 'CONFIRMATION';

type CancelSubmitButtonProps = {
  onClick: () => void;
  disabled: boolean;
  isSubmitting: boolean;
  buttonText: string;
};

export function CancelSubmitButton({
  onClick,
  disabled,
  isSubmitting,
  buttonText,
}: CancelSubmitButtonProps): ReactElement {
  return (
    <Button
      className="c-button c-button--primary btn btn-dark d-flex align-items-center gap-2"
      onClick={onClick}
      disabled={disabled}
      style={{
        borderRadius: '6px',
        padding: '10px 24px',
      }}
    >
      <span>{isSubmitting ? 'Processing...' : buttonText}</span>
      {!isSubmitting && (
        <span
          className="ms-2"
          style={{
            fontSize: '1.1em',
            marginLeft: '8px',
          }}
        >
          &rarr;
        </span>
      )}
    </Button>
  );
}

type Props = {
  show: boolean;
  onClose: () => void;
  handleErrors: () => void;
};

function CancelModalContainer({
  show,
  onClose,
  handleErrors,
}: Props): ReactElement {
  const [shuffledOptions] = useState(getOptionsForCancel);
  const [stepSequence] = useState<CancelStep[]>(['REASON_CAPTURE', 'REBUTTAL', 'CONFIRMATION']);
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelReasonDetail, setCancelReasonDetail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const isMobile = useMediaQuery('(max-width: 768px)');
  const navigate = useNavigate();

  const isMounted = useRef(true);
  useEffect(() => () => {
    isMounted.current = false;
  }, []);

  useEffect(() => {
    if (isMobile && show) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [isMobile, show]);

  const currentStep = stepSequence[currentStepIndex];

  const handleCancelReasonChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setCancelReason(e.target.id);
  };

  const {
    reload,
    currentUser,
  } = useCurrentUser();

  const handleDeactivation = async () => {
    setIsSubmitting(true);

    const payload = {
      subscription: {
        status: 'cancelled',
        subscription_cancellation_attributes: {
          reason: cancelReason,
          reason_details: cancelReasonDetail,
          prepared_follow_up_opt_in: false,
        },
      },
    };

    try {
      const isCurrentlyPaused = currentUser?.subscription?.status === 'paused';

      const response = await patch(`${ApiRoute.DEACTIVATE}`, payload);

      if (response.ok) {
        postMessage(WebViewMessage.SUBSCRIPTION_CANCELLED);

        if (isMounted.current) {
          reload();
          if (!isCurrentlyPaused) {
            navigate(Route.UPCOMING_MENUS, {
              state: {
                invalidate: true,
              },
            });
          }
          onClose();
        }
      } else {
        throw new Error('Deactivation failed');
      }
    } catch (e) {
      console.error('CRASH IDENTIFIED INSIDE MODAL:', e);
      if (isMounted.current) {
        handleErrors();
        onClose();
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    }
  };

  const handleNextStep = () => {
    const hasRebuttal = cancelOptions.find((o) => o.id === cancelReason)?.rebuttal;
    if (!hasRebuttal) {
      setCurrentStepIndex(stepSequence.indexOf('CONFIRMATION'));
    } else {
      setCurrentStepIndex((prev) => prev + 1);
    }
  };

  const handleFinalConfirmationClose = () => {
    handleDeactivation();
  };

  const handleClose = () => {
    if (currentStep === 'CONFIRMATION') {
      handleFinalConfirmationClose();
    } else {
      onClose();
    }
  };

  const title = currentStep === 'REBUTTAL'
    ? cancelOptions.find((o) => o.id === cancelReason)?.rebuttal?.title
    : 'Cancel Your Subscription';

  const bodyContent = (
    <>
      {isMobile && (
        <div style={{
          fontFamily: 'Mabry',
          fontWeight: 500,
          fontSize: '26px',
          lineHeight: '120%',
          paddingBottom: '12px',
        }}
        >
          {title}
        </div>
      )}
      {currentStep === 'REASON_CAPTURE' && (
        <div
          className="c-subscription-management-option__form p-2"
          style={isMobile ? { paddingLeft: '8px' } : undefined}
        >
          <p className="u-text u-text--lg">Could you tell us why you&apos;re cancelling?</p>
          <p className="u-text text-muted mb-4">
            It&apos;ll take just a few seconds, and your
            feedback will help us improve.
          </p>

          <form onSubmit={(e) => e.preventDefault()}>
            <fieldset className="c-form-group">
              <div className="c-input-radio-group" aria-label="Cancel Subscription Reason">
                {shuffledOptions.map((option) => (
                  <div key={option.id} className="mb-2">
                    <RadioButton
                      id={option.id}
                      label={option.label}
                      name="reason"
                      onChange={handleCancelReasonChange}
                    />

                    {cancelReason === option.id && (
                      <div
                        className="ps-5 mt-2 mb-3 animate-fade-in text-start"
                        style={{
                          borderLeft: '2px solid #cccccc',
                          marginLeft: '12px',
                          paddingLeft: '12px',
                        }}
                      >
                        <div className="c-form-group text required w-100">
                          <label className="text required w-100 text-start d-block">
                            <span
                              className="fw-bold d-block mb-1 text-start"
                              style={{ fontSize: '14px' }}
                            >
                              Please tell us a little more*
                            </span>
                            <textarea
                              className="text required form-control"
                              rows={4}
                              value={cancelReasonDetail}
                              onChange={(e) => setCancelReasonDetail(e.target.value)}
                              placeholder="Share your feedback"
                            />
                          </label>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </fieldset>
          </form>

          <div className="d-flex flex-column align-items-start mt-4" style={{ gap: '20px' }}>
            <CancelSubmitButton
              onClick={handleNextStep}
              disabled={cancelReason.length === 0 || cancelReasonDetail.trim().length === 0
                || isSubmitting}
              isSubmitting={isSubmitting}
              buttonText="Next"
            />

            <a
              href="#close"
              role="button"
              onClick={(e) => {
                e.preventDefault();
                onClose();
              }}
              className="text-dark"
              style={{
                fontSize: '14px',
                textDecoration: 'underline',
                cursor: 'pointer',
              }}
            >
              Actually, I&apos;d Like To Keep My Account
            </a>
          </div>
        </div>
      )}
      {currentStep === 'REBUTTAL' && (
        <CancelRebuttalModal
          reason={cancelReason}
          onContinueToCancel={() => setCurrentStepIndex(stepSequence.indexOf('CONFIRMATION'))}
        />
      )}
      {currentStep === 'CONFIRMATION' && (
        <div className="text-center py-3">
          <p
            className="u-text fw-bold"
            style={{
              fontSize: '16px',
              marginBottom: '1.5rem',
            }}
          >
            Your subscription will be cancelled.
          </p>
          <p className="u-text text-muted mb-4">
            Thank you so much for trying us for your
            plant-based meal needs!
          </p>
          <p>
            If you change your mind, you&apos;ll always be able to reactivate your subscription
            at any time by logging into the same account.
          </p>
          <Button
            className="c-button c-button--primary btn btn-dark w-100"
            onClick={handleFinalConfirmationClose}
          >
            Confirm
          </Button>
        </div>
      )}
    </>
  );

  if (isMobile) {
    return (
      <Drawer
        opened={show}
        onClose={handleClose}
        position="bottom"
        overlayBlur={6}
        overlayOpacity={0.6}
        size="80%"
        lockScroll
        classNames={{
          body: styles.modal_body,
          closeButton: styles.modal_close,
        }}
        styles={{
          drawer: {
            display: 'flex',
            flexDirection: 'column',
            borderRadius: '16px 16px 0 0',
          },
          header: {
            marginBottom: 0,
            padding: '16px 16px 0',
          },
          body: {
            flex: 1,
            minHeight: 0,
            overflowY: 'auto',
            padding: '16px',
          },
          closeButton: {
            color: 'black',
            background: 'none',
            border: 'none',
            boxShadow: 'none',
            cursor: 'pointer',
            '&:hover': {
              background: 'none',
              color: 'black',
            },
            '&:focus': {
              background: 'none',
              color: 'black',
              outline: 'none',
            },
            '&:active': {
              background: 'none',
              color: 'black',
            },
          },
        }}
      >
        {bodyContent}
      </Drawer>
    );
  }

  return (
    <Modal
      show={show}
      onHide={handleClose}
      centered
      dialogClassName={styles.cancel_modal_dialog}
      contentClassName={styles.cancel_modal_content}
    >
      <Modal.Header closeButton>
        <Modal.Title className={styles.heading}>{title}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {bodyContent}
      </Modal.Body>
    </Modal>
  );
}

export default CancelModalContainer;
